import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  calcExportData,
  calcReportParamsSection,
  resolveReportParams
} from '@aytm/app/stats/page/question-views/info-view/components/charts/histogram/lib/report-params'

// Owns the histogram's bin count. Seeded from the hg<key> section and re-seeded whenever it
// changes outside the chart.
//
// The page view never parses the section: it receives an api on mount and pulls the payloads it
// needs (the section to write, the export payload) when a change is announced through
// onStateChange.
export default function useHistogramState({ params, format, bars, onReady, onStateChange }) {
  const [state, setState] = useState(() => resolveReportParams({ params, format, bars }))

  const snapshot = useRef()
  snapshot.current = { state, context: { format, bars } }

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(
    () => ({
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => calcReportParamsSection(snapshot.current.state, snapshot.current.context),
      getExportData: () => calcExportData(snapshot.current.state),
      update: onModelChange
    }),
    [onModelChange]
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  const sectionKey = JSON.stringify(params?.hg ?? null)
  const lastSectionKey = useRef(sectionKey)
  useEffect(() => {
    if (lastSectionKey.current === sectionKey) return
    lastSectionKey.current = sectionKey

    const { format, bars } = snapshot.current.context

    pendingReason.current = 'seed'
    setState(resolveReportParams({ params: { hg: JSON.parse(sectionKey) }, format, bars }))
  }, [sectionKey])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  return { ...state, onModelChange }
}
